import { ErrorCode, type ApiErrorData } from '@shared/api';
import { Button, Icon, IconColorsEnum, TextColorsEnum, Modal, Text } from '@shared/widgets';
import AttentionIcon from '@assets/icons/attention.svg';

const Type = {
  CreateRecipient: 'createRecipient',
  UpdateRecipient: 'updateRecipient',
  CreateTransfer: 'createTransfer',
  UpdateTransfer: 'updateTransfer',
} as const;
type Type = (typeof Type)[keyof typeof Type];

interface ModalConfig {
  title: string;
  iconColor: IconColorsEnum;
  textColor?: TextColorsEnum;
  actions: Record<Type, { question: string; cancel: string; submit: string }>;
}

const CONFIG: Record<string, ModalConfig> = {
  [ErrorCode.TRP_VAL_008]: {
    title: 'Attention',
    iconColor: 'red',
    textColor: 'red',
    actions: {
      createRecipient: { question: 'Do you want to save the recipients details?', cancel: 'Cancel', submit: 'Yes, save recipient' },
      updateRecipient: { question: 'Do you want to save the recipients details?', cancel: 'Cancel', submit: 'Yes, save recipient' },
      createTransfer: { question: 'Do you want the payment to be executed?', cancel: 'Cancel payment', submit: 'Continue payment' },
      updateTransfer: { question: 'Do you want the payment to be executed?', cancel: 'Cancel payment', submit: 'Continue payment' },
    },
  },
  [ErrorCode.TRP_VAL_009]: {
    title: 'Name partially matches',
    iconColor: 'yellow',
    actions: {
      createRecipient: { question: 'Do you want to amend the recipients details?', cancel: 'Yes, update recipient', submit: 'No, save details' },
      updateRecipient: { question: 'Do you want to amend the recipients details?', cancel: 'Yes, update recipient', submit: 'No, save details' },
      createTransfer: {
        question: 'Do you want to amend the details before continuing with the payment?',
        cancel: 'Update recipient',
        submit: 'Continue payment',
      },
      updateTransfer: { question: 'Do you want the payment to be executed?', cancel: 'Cancel payment', submit: 'Continue payment' },
    },
  },
  [ErrorCode.TRP_VAL_010]: {
    title: 'Recipient name does not match',
    iconColor: 'red',
    actions: {
      createRecipient: { question: 'Do you want to amend the recipients details?', cancel: 'Yes, update recipient', submit: 'No, save details' },
      updateRecipient: { question: 'Do you want to amend the recipients details?', cancel: 'Yes, update recipient', submit: 'No, save details' },
      createTransfer: {
        question: 'Do you want to amend the details before continuing with the payment?',
        cancel: 'Update recipient',
        submit: 'Continue payment',
      },
      updateTransfer: { question: 'Do you want the payment to be executed?', cancel: 'Cancel payment', submit: 'Continue payment' },
    },
  },
  [ErrorCode.TRP_VAL_011]: {
    title: 'Attention',
    iconColor: 'red',
    textColor: 'red',
    actions: {
      createRecipient: { question: 'Do you want to save the recipients details?', cancel: 'Cancel', submit: 'Yes, save recipient' },
      updateRecipient: { question: 'Do you want to save the recipients details?', cancel: 'Cancel', submit: 'Yes, save recipient' },
      createTransfer: { question: 'Do you want the payment to be executed?', cancel: 'Cancel payment', submit: 'Continue payment' },
      updateTransfer: { question: 'Do you want the payment to be executed?', cancel: 'Cancel payment', submit: 'Continue payment' },
    },
  },
};

interface RecipientVerificationModalProps {
  isOpen: boolean;
  onClose?: () => void;
  type: Type;
  loading?: boolean;
  error?: ApiErrorData | null;
  onCancel?: () => void;
  onSubmit?: () => void;
}

export const RecipientVerificationModal = (props: RecipientVerificationModalProps) => {
  if (!props.isOpen || !props.error?.errorCode || !CONFIG[props.error.errorCode]) return null;
  const config = CONFIG[props.error.errorCode];
  const actions = config.actions[props.type];

  return (
    <Modal isOpen onClose={props.onClose} className='px-[30px] py-9' headerClassName='mb-0' width='468px'>
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-2'>
          <Icon color={config.iconColor} icon={<AttentionIcon className='mx-auto' />} />
          <Text className='text-center' tag='h3' size='3xl' color={config.textColor}>
            {config.title}
          </Text>
        </div>
        <div className='flex flex-col gap-2 px-4 text-justify'>
          <Text size='lg' color='secondary-light'>
            {props.error.message}
          </Text>
          <Text size='lg' color='secondary-light'>
            {actions.question}
          </Text>
        </div>
        <div className='flex gap-4'>
          <Button fullWidth color='outlined' disabled={props.loading} onClick={props.onCancel || props.onClose}>
            {actions.cancel}
          </Button>
          <Button fullWidth color='primary' disabled={props.loading} onClick={props.onSubmit}>
            {actions.submit}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
